/**
 * Centralized date and time formatting utilities
 */

/**
 * Parse a datetime string from the API, treating timezone-naive strings as UTC.
 * Postgres TIMESTAMP WITHOUT TIME ZONE returns strings like "2026-04-01T10:00:00"
 * with no timezone suffix. Browsers interpret those as local time, causing drift.
 * This helper appends "Z" when no timezone info is present so the value is
 * always interpreted as UTC.
 */
export function parseApiDateTime(dateStr: string): Date {
  if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'Z')
  }
  return new Date(dateStr)
}

/**
 * Format a date string to a relative time description
 * Returns "Today", "Yesterday", "X days ago", or a formatted date
 */
export function formatDate(dateStr: string): string {
  const date = parseApiDateTime(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

/**
 * Format seconds into HH:MM:SS format
 */
export function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

/**
 * Format a timestamp to time string based on timeframe
 * For hour/day: shows HH:MM, for week: shows Mon DD
 */
export function formatTimeByTimeframe(time: number, timeframe: string): string {
  const date = new Date(time)
  if (timeframe === 'hour') return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (timeframe === 'day') return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

/**
 * Format an ISO string to HH:MM format (24-hour)
 */
export function formatTimeFromISO(isoString: string): string {
  try {
    const date = parseApiDateTime(isoString)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  } catch {
    return '--:--'
  }
}

/**
 * Format a timestamp to short date and time
 * Example: "Jan 15, 3:45 PM"
 */
export function formatShortDateTime(timestamp: string): string {
  try {
    return parseApiDateTime(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return timestamp
  }
}

/**
 * Format a date with weekday
 * Example: "Mon, Jan 15"
 */
export function formatDateWithWeekday(dateStr: string): string {
  try {
    const parsed = parseApiDateTime(dateStr)
    return parsed.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Format a date to short date string
 * Example: "1/15/2024"
 */
export function formatShortDate(dateStr: string): string {
  try {
    return parseApiDateTime(dateStr).toLocaleDateString()
  } catch {
    return dateStr
  }
}

/**
 * Format a date to short time string
 * Example: "3:45 PM"
 */
export function formatShortTime(dateStr: string): string {
  try {
    return parseApiDateTime(dateStr).toLocaleTimeString()
  } catch {
    return dateStr
  }
}

/**
 * Extract the date portion (YYYY-MM-DD in local timezone) from a datetime string.
 * Used for grouping tasks by date when due_date is a full datetime.
 */
export function extractDatePortion(datetimeStr: string): string {
  const dt = parseApiDateTime(datetimeStr)
  return dt.toLocaleDateString('en-CA') // Returns YYYY-MM-DD
}

/**
 * Check whether a due_date datetime has a user-specified time (not midnight UTC).
 */
export function hasExplicitTime(dueDateStr: string): boolean {
  const dt = parseApiDateTime(dueDateStr)
  return dt.getUTCHours() !== 0 || dt.getUTCMinutes() !== 0
}

/**
 * Format a due_date time portion for display (e.g. "2:30 PM").
 * Returns null if no explicit time was set (midnight UTC).
 */
export function formatDueTime(dueDateStr: string): string | null {
  if (!hasExplicitTime(dueDateStr)) return null
  const dt = parseApiDateTime(dueDateStr)
  return dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/**
 * Format a timestamp to a relative time-ago label
 * Returns "just now", "5m", "2h", "3d", "2mo", etc.
 */
export function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const diff = Date.now() - parseApiDateTime(dateStr).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = Math.floor(days / 30)
  return `${months}mo`
}

/**
 * Convert a Date object to YYYY-MM-DD string in local timezone.
 * CRITICAL: This does NOT use toISOString() which converts to UTC and can shift
 * the date by one day for users in western timezones (PST, MST, etc.).
 */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Given a local date string (YYYY-MM-DD), returns the UTC ISO boundaries for that
 * local day: since_utc = local midnight → UTC, until_utc = next local midnight → UTC.
 *
 * Use this when sending date-range queries to the API so the backend filters by the
 * user's actual local day, not UTC midnight.
 *
 * Example (CDT = UTC-5):
 *   localDayBoundsUTC("2026-04-14")
 *   → { since_utc: "2026-04-14T05:00:00.000Z", until_utc: "2026-04-15T05:00:00.000Z" }
 */
export function localDayBoundsUTC(localDateStr: string): { since_utc: string; until_utc: string } {
  const start = new Date(localDateStr + 'T00:00:00')  // local midnight
  const end = new Date(localDateStr + 'T00:00:00')
  end.setDate(end.getDate() + 1)                      // next local midnight
  return { since_utc: start.toISOString(), until_utc: end.toISOString() }
}
